const ENGAGE_BASE = '/dashboard/engage';
const REPUTATION_BASE = '/dashboard/reputation';
const VOICE_BASE = '/dashboard/voice';
const AI_TOOLS_BASE = '/dashboard/ai-tools';
const AI_CREDENTIALS_BASE = `${AI_TOOLS_BASE}/credentials`;
const AI_SKILLS_BASE = `${AI_TOOLS_BASE}/skills`;
const AGENTS_BASE = '/dashboard/agents';

/**
 * Relative paths for the automations and segments routes, which Engage and Reputation both
 * mount under their own base. Resolve them through the feature surface rather than assuming a base.
 */
export const featureNestedRoutes = {
  AUTOMATIONS_EDIT: 'automations/edit/:automation-id',
  SEGMENTS_NEW: 'segments/new',
  SEGMENTS_EDIT: 'segments/edit/:canonicalVersionId',
} as const;

/** Relative paths for the sections the agent builder mounts under its own base. */
export const agentBuilderNestedRoutes = {
  VOICE_TEMPLATE: 'voice-template',
  TENANT_PREVIEW: 'tenant-preview',
  SKILLS: 'skills',
  GUIDANCE: 'guidance',
  ESCALATIONS: 'escalations',
  TOOLS: 'tools',
  NEW_COMPONENT: 'new',
  COMPONENT: ':componentId',
  NEW_TOOL: 'new',
  TOOL: ':id',
} as const;

export const appRoutes = {
  TENANT_DETAIL: '/dashboard/tenants/:id',
  ENGAGE_SETUP: ENGAGE_BASE,
  ENGAGE_TENANTS: `${ENGAGE_BASE}/tenants`,
  ENGAGE_AUTOMATIONS: `${ENGAGE_BASE}/automations`,
  ENGAGE_SEGMENTS: `${ENGAGE_BASE}/segments`,
  ENGAGE_SEGMENTS_NEW: `${ENGAGE_BASE}/${featureNestedRoutes.SEGMENTS_NEW}`,
  REPUTATION_SETUP: REPUTATION_BASE,
  REPUTATION_AUTOMATIONS: `${REPUTATION_BASE}/automations`,
  REPUTATION_SEGMENTS: `${REPUTATION_BASE}/segments`,
  REPUTATION_SEGMENTS_NEW: `${REPUTATION_BASE}/${featureNestedRoutes.SEGMENTS_NEW}`,
  VOICE: VOICE_BASE,
  AI_AGENTS: AI_TOOLS_BASE,
  AI_TOOLS: `${AI_TOOLS_BASE}/tools`,
  AI_TOOL_NEW: `${AI_TOOLS_BASE}/new`,
  AI_TOOL_EDIT: `${AI_TOOLS_BASE}/:id`,
  AI_CREDENTIALS: AI_CREDENTIALS_BASE,
  AI_SKILLS: AI_SKILLS_BASE,
  AGENTS: AGENTS_BASE,
  AGENT_VOICE_TEMPLATE: `${AGENTS_BASE}/${agentBuilderNestedRoutes.VOICE_TEMPLATE}`,
  AGENT_VOICE_TEMPLATE_TENANT_PREVIEW: `${AGENTS_BASE}/${agentBuilderNestedRoutes.VOICE_TEMPLATE}/${agentBuilderNestedRoutes.TENANT_PREVIEW}`,
  AGENT_SKILLS: `${AGENTS_BASE}/${agentBuilderNestedRoutes.SKILLS}`,
  AGENT_GUIDANCE: `${AGENTS_BASE}/${agentBuilderNestedRoutes.GUIDANCE}`,
  AGENT_ESCALATIONS: `${AGENTS_BASE}/${agentBuilderNestedRoutes.ESCALATIONS}`,
  AGENT_TOOLS: `${AGENTS_BASE}/${agentBuilderNestedRoutes.TOOLS}`,
  AGENT_TOOL_NEW: `${AGENTS_BASE}/${agentBuilderNestedRoutes.TOOLS}/${agentBuilderNestedRoutes.NEW_TOOL}`,
  AGENT_TOOL: `${AGENTS_BASE}/${agentBuilderNestedRoutes.TOOLS}/${agentBuilderNestedRoutes.TOOL}`,
} as const;

export const buildAiToolEditPath = (id: string): string => appRoutes.AI_TOOL_EDIT.replace(':id', id);

/** The agent builder's page for one saved tool. */
export const buildAgentToolPath = (id: string): string => appRoutes.AGENT_TOOL.replace(':id', id);

/** Build navigation path by replacing :automation-id param, under the given feature base. */
export const buildAutomationEditPath = (basePath: string, automationId: string): string =>
  `${basePath}/${featureNestedRoutes.AUTOMATIONS_EDIT}`.replace(':automation-id', automationId);

/** Build navigation path by replacing :canonicalVersionId param, under the given feature base. */
export const buildSegmentEditPath = (basePath: string, canonicalVersionId: string): string =>
  `${basePath}/${featureNestedRoutes.SEGMENTS_EDIT}`.replace(':canonicalVersionId', canonicalVersionId);
